import type { TabDot } from '../types/songbook'
import {
  AUDIO_MASTER_GAIN,
  AUDIO_STRING_GAIN,
  AUDIO_HARMONIC_GAIN,
  AUDIO_DURATION,
  AUDIO_STRUM_DELAY,
} from '../config/songbook'

// Standard guitar tuning frequencies (string 0=high e, 5=low E)
const OPEN_FREQUENCIES = [
  329.63, // e4 (string 0)
  246.94, // B3 (string 1)
  196.00, // G3 (string 2)
  146.83, // D3 (string 3)
  110.00, // A2 (string 4)
  82.41,  // E2 (string 5)
]

function getFrequency(stringIndex: number, fret: number, startFret: number): number {
  const base = OPEN_FREQUENCIES[stringIndex]
  // fret -1 = open string (use base frequency).
  // dot.fret is the 0-indexed column inside the diagram; the displayed
  // roman numeral is startFret + column (see GuitarTab.tsx), so the
  // played semitone offset must match: startFret + fret.
  const actualFret = fret === -1 ? 0 : startFret + fret
  return base * Math.pow(2, actualFret / 12)
}

let audioCtx: AudioContext | null = null

function getAudioContext(): AudioContext {
  if (!audioCtx) {
    audioCtx = new AudioContext()
  }
  return audioCtx
}

/**
 * Play a guitar chord from tab dots.
 * Uses Web Audio API with a plucked-string-like envelope.
 */
export function playChord(dots: TabDot[], startFret: number = 1) {
  if (dots.length === 0) return

  const ctx = getAudioContext()
  const now = ctx.currentTime

  // Master gain
  const master = ctx.createGain()
  master.gain.setValueAtTime(AUDIO_MASTER_GAIN, now)
  master.gain.exponentialRampToValueAtTime(0.001, now + AUDIO_DURATION)
  master.connect(ctx.destination)

  // Strum delay: slight offset per string for realistic strum feel
  dots
    .sort((a, b) => b.string - a.string) // strum from low to high
    .forEach((dot, i) => {
      const freq = getFrequency(dot.string, dot.fret, startFret)
      const strumDelay = i * AUDIO_STRUM_DELAY

      // Fundamental oscillator
      const osc = ctx.createOscillator()
      osc.type = 'triangle'
      osc.frequency.setValueAtTime(freq, now + strumDelay)

      // Per-string gain with quick attack
      const gain = ctx.createGain()
      gain.gain.setValueAtTime(0, now + strumDelay)
      gain.gain.linearRampToValueAtTime(AUDIO_STRING_GAIN, now + strumDelay + 0.005)
      gain.gain.exponentialRampToValueAtTime(0.001, now + strumDelay + AUDIO_DURATION)

      // Add slight harmonics for richer sound
      const osc2 = ctx.createOscillator()
      osc2.type = 'sine'
      osc2.frequency.setValueAtTime(freq * 2, now + strumDelay)

      const gain2 = ctx.createGain()
      gain2.gain.setValueAtTime(0, now + strumDelay)
      gain2.gain.linearRampToValueAtTime(AUDIO_HARMONIC_GAIN, now + strumDelay + 0.005)
      gain2.gain.exponentialRampToValueAtTime(0.001, now + strumDelay + AUDIO_DURATION * 0.6)

      osc.connect(gain)
      osc2.connect(gain2)
      gain.connect(master)
      gain2.connect(master)

      osc.start(now + strumDelay)
      osc.stop(now + strumDelay + AUDIO_DURATION)
      osc2.start(now + strumDelay)
      osc2.stop(now + strumDelay + AUDIO_DURATION)
    })
}
